import { collection, doc, getDoc, onSnapshot, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useThemeContext } from "../../../context/ThemeProvider";
import { useUserContext } from "../../../context/UserProvider";
import { db } from "../../../firebase";
import { RaceTitle } from "../../util/Components";
import { AccountIcon } from "../../util/Icons";
import ActivityFeed from "./ActivityFeed";
import { ActivityForm } from "./ActivityForm";
import { MANUAL_ACTIVITY_ORIGINS } from "../../../features/manualActivities/ManualActivityForm";
import DropDownMenu from "../../util/DropDownMenu";
import CreateImage, { getPresetFinisherBadge } from "./CreateImage";
import { saveRaceReady } from "../../RacePage/RaceReady/raceReadyStore";
import { getLocalTodayKey } from "../ChallengesPage/challengePageUtils";

export default function DashboardPage() {
  const { user, userDetails, userData, userRaces, linkedDevice, canSocialFeed } = useUserContext();
  const { colors } = useThemeContext();
  const [activityFeed, setActivityFeed] = useState([]);
  const [userTimesDoc, setUserTimesDoc] = useState(null);
  const [teamMembers, setTeamMembers] = useState(new Map());
  const [teamDoc, setTeamDoc] = useState(null);
  const [showTeamCode, setShowTeamCode] = useState(true);
  const [submissionState, setSubmissionState] = useState("idle");
  const [submitError, setSubmitError] = useState("");
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [totalDistance, setTotalDistance] = useState(0);
  const fullscreenMedalRef = useRef();
  const activityFormCloseTimeoutRef = useRef(null);
  const activeSubmissionIdRef = useRef(0);
  const { raceId } = useParams();
  const selectedRace = userRaces?.get(`price_${raceId}`);
  const showTrackingToggle = user?.uid === "Bb0ztU5QGAaTi9my6mBQJsRVeGx2" &&
    Boolean(linkedDevice && linkedDevice !== "none" && selectedRace?.legId);
  const submitManualActivityCallable = httpsCallable(getFunctions(), "submitManualActivity");

  function clearActivityFormCloseTimeout() {
    if (activityFormCloseTimeoutRef.current) {
      window.clearTimeout(activityFormCloseTimeoutRef.current);
      activityFormCloseTimeoutRef.current = null;
    }
  }

  const closeActivityForm = () => {
    clearActivityFormCloseTimeout();
    activeSubmissionIdRef.current += 1;
    setShowActivityForm(false);
  };
  const handleActivityFormExited = () => {
    clearActivityFormCloseTimeout();
    setSubmissionState("idle");
    setSubmitError("");
  };

  const [raceDoc, setRaceDoc] = useState(null);
  const navigate = useNavigate();

  const [raceData, setRaceData] = useState(null);
  const bibLink = raceData?.metadata?.bib_link || "";
  const resolvedImagePreset =
    selectedRace?.raceInfo?.metadata?.imagePreset ||
    selectedRace?.imagePreset ||
    raceData?.metadata?.imagePreset ||
    raceData?.imagePreset ||
    raceDoc?.metadata?.imagePreset ||
    raceDoc?.imagePreset ||
    "";
  const presetFinisherBadge = getPresetFinisherBadge(resolvedImagePreset);
  const finisherBadge = presetFinisherBadge || raceDoc?.finisher_badge;
  async function getCurrentRace(raceId) {
    const raceDoc = await getDoc(doc(db, "stripe_products", raceId));
    setRaceData(raceDoc.data());
  }
  // Ticks "Open your dashboard" on the confirmation page's race-ready checklist.
  useEffect(() => {
    if (!user?.uid || !selectedRace?.legId || selectedRace?.raceReady?.dashboardAt) return;
    saveRaceReady(user.uid, selectedRace.legId, { dashboardAt: "now" });
  }, [user?.uid, selectedRace?.legId, selectedRace?.raceReady?.dashboardAt]);
  useEffect(() => {
    async function getRaceDoc() {
      const raceDocRef = await getDoc(doc(db, "races", `price_${raceId}`));
      if (raceDocRef.exists()) {
        setRaceDoc(raceDocRef.data());
      }
    }
    getRaceDoc();
    window.scrollTo(0, 0);
  }, [raceId]);

  useEffect(() => {
    if (selectedRace?.raceId) {
      getCurrentRace(selectedRace.raceId);
    } else {
      setRaceData(null);
    }
  }, [selectedRace?.raceId]);

  useEffect(() => {
    if (
      !user?.uid ||
      !selectedRace?.legId ||
      !linkedDevice ||
      linkedDevice === "none"
    ) {
      return;
    }

    const shouldEnableByDefault = typeof selectedRace.stravaLink !== "boolean";
    const shouldSaveFirstEnabledAt =
      selectedRace.stravaLink !== false && !selectedRace.stravaLinkFirstEnabledAt;
    const shouldSaveUpdatedAt =
      selectedRace.stravaLink !== false && !selectedRace.stravaLinkUpdatedAt;

    if (!shouldEnableByDefault && !shouldSaveFirstEnabledAt && !shouldSaveUpdatedAt) {
      return;
    }

    const timestamp = serverTimestamp();
    const updates = {};
    if (shouldEnableByDefault) {
      updates[`subscribedRaces.${selectedRace.legId}.stravaLink`] = true;
    }
    if (shouldEnableByDefault || shouldSaveFirstEnabledAt) {
      updates[`subscribedRaces.${selectedRace.legId}.stravaLinkFirstEnabledAt`] = timestamp;
    }
    if (shouldEnableByDefault || shouldSaveUpdatedAt) {
      updates[`subscribedRaces.${selectedRace.legId}.stravaLinkUpdatedAt`] = timestamp;
    }

    updateDoc(doc(db, "users", user.uid), updates).catch((error) => {
      console.error("Unable to initialize automatic race tracking", error);
    });
  }, [
    linkedDevice,
    selectedRace?.legId,
    selectedRace?.stravaLink,
    selectedRace?.stravaLinkFirstEnabledAt,
    selectedRace?.stravaLinkUpdatedAt,
    user?.uid,
  ]);

  useEffect(() => {
    return () => {
      clearActivityFormCloseTimeout();
      activeSubmissionIdRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (submissionState !== "submitting") {
      return undefined;
    }

    function handleBeforeUnload(event) {
      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [submissionState]);

  const raceDistance = parseInt(selectedRace?.raceInfo?.distance);

  // const totalDistance = parseInt(selectedRace.raceInfo.distance) || 0; //activityFeed.reduce((total, current) => total + parseInt(current.value.distance), 0);

  //   console.log(teamMembers, teamMembersArray);
  async function addTimeToRace(confirmedActivity) {
    const submissionId = activeSubmissionIdRef.current + 1;
    activeSubmissionIdRef.current = submissionId;
    setSubmitError("");
    setSubmissionState("submitting");
    try {
      const activityData = confirmedActivity || {};
      const distanceKm = parseFloat(activityData.distance ?? activityData.distanceKm) || 0;
      const hours = parseInt(activityData.hours ?? activityData.durationHours, 10) || 0;
      const minutes = parseInt(activityData.minutes ?? activityData.durationMinutes, 10) || 0;
      const seconds = parseInt(activityData.seconds ?? activityData.durationSeconds, 10) || 0;
      const steps = parseInt(activityData.steps, 10) || 0;
      const totalMinutes = hours * 60 + minutes + seconds / 60;
      const activityNoteParts = [
        selectedRace?.raceInfo?.name || "Race",
        selectedRace?.raceInfo?.legName || selectedRace?.legId || "",
        activityData.note || "",
      ].filter(Boolean);
      await submitManualActivityCallable({
        origin: MANUAL_ACTIVITY_ORIGINS.CHALLENGE_DASHBOARD,
        challengeLegIds: [selectedRace?.legId].filter(Boolean),
        clientSubmissionId: activityData.clientSubmissionId,
        entryMode: activityData.entryMode,
        date: activityData.date ?? activityData.activityDate,
        minutes: totalMinutes,
        distanceKm,
        steps,
        durationHours: hours,
        durationMinutes: minutes,
        durationSeconds: seconds,
        label: activityData.label || (steps > 0 ? "manual_steps" : "manual_race"),
        note: activityNoteParts.join(" | "),
      });

      if (activeSubmissionIdRef.current !== submissionId) {
        return;
      }
      setSubmissionState("success");
      clearActivityFormCloseTimeout();
      activityFormCloseTimeoutRef.current = window.setTimeout(() => {
        if (activeSubmissionIdRef.current === submissionId) {
          closeActivityForm();
        }
      }, 1000);
    } catch (err) {
      console.error("Error submitting manual activity", err?.message || err);
      if (activeSubmissionIdRef.current !== submissionId) {
        return;
      }
      setSubmitError(err?.message || "Unable to submit activity right now. Please try again.");
      setSubmissionState("idle");
    }
  }

  async function getActivity() {
    if (!selectedRace) return;
    const activityRef = await getDoc(doc(db, "races", selectedRace.legId, "subscribedUsers", user.uid));
    let newActivity = [];
    if (activityRef.exists()) {
      if (activityRef.get("times")) {
        for (const [key, value] of Object.entries(activityRef.get("times"))) {
          newActivity.push({ key, value });
          // console.log(`${key}: ${value}`);
        }
      }
      //   setBibNumber(activityRef.data().bibNumber);
      setTotalDistance(activityRef.data().totalDistance || 0);
      setUserTimesDoc(activityRef);
    }
    setActivityFeed(newActivity);
  }
  useEffect(() => {
    if (!selectedRace?.legId || !user?.uid) {
      setActivityFeed([]);
      setUserTimesDoc(null);
      setTotalDistance(0);
      setTeamMembers(new Map());
      setTeamDoc(null);
      return undefined;
    }

    const activityRef = doc(db, "races", selectedRace.legId, "subscribedUsers", user.uid);
    const unsubActivity = onSnapshot(
      activityRef,
      (activitySnapshot) => {
        const nextActivityFeed = [];
        if (activitySnapshot.exists()) {
          const times = activitySnapshot.get("times") || {};
          Object.entries(times).forEach(([key, value]) => {
            nextActivityFeed.push({ key, value });
          });
          setTotalDistance(activitySnapshot.data().totalDistance || 0);
          setUserTimesDoc(activitySnapshot);
        } else {
          setTotalDistance(0);
          setUserTimesDoc(null);
        }
        setActivityFeed(nextActivityFeed);
      },
      (error) => {
        console.error("Error loading race activity", error);
        setActivityFeed([]);
        setTotalDistance(0);
        setUserTimesDoc(null);
      }
    );

    let unsubTeamMembers = () => {};
    let unsubTeamDoc = () => {};

    if (selectedRace.teamName !== "noTeam") {
      const teamMembersQuery = query(collection(db, "races", selectedRace.legId, "subscribedUsers"), where("teamName", "==", selectedRace.teamName));
      unsubTeamMembers = onSnapshot(
        teamMembersQuery,
        (teamMembersDocs) => {
          const teamMembersMap = new Map();
          teamMembersDocs.forEach((member) => {
            teamMembersMap.set(member.id, member.data());
          });
          setTeamMembers(teamMembersMap);
        },
        (error) => {
          console.error("Error loading team members", error);
          setTeamMembers(new Map());
        }
      );

      unsubTeamDoc = onSnapshot(
        doc(db, "races", `price_${raceId}`, "teams", selectedRace.teamName),
        (teamDocSnapshot) => {
          setTeamDoc(teamDocSnapshot.exists() ? teamDocSnapshot.data() : null);
        },
        (error) => {
          console.error("Error loading team data", error);
          setTeamDoc(null);
        }
      );
    } else {
      setTeamMembers(new Map());
      setTeamDoc(null);
    }

    return () => {
      unsubActivity();
      unsubTeamMembers();
      unsubTeamDoc();
    };
  }, [raceId, selectedRace?.legId, selectedRace?.teamName, user?.uid]);

  if (!!userRaces && !selectedRace) {
    return <Navigate to="/" replace={true} />;
  }
  if (!selectedRace) {
    return;
  }

  async function switchStravaCheck(nextStravaLink) {
    if (!showTrackingToggle) return;
    const timestamp = serverTimestamp();
    const updates = {
      [`subscribedRaces.${selectedRace.legId}.stravaLink`]: nextStravaLink,
      [`subscribedRaces.${selectedRace.legId}.stravaLinkUpdatedAt`]: timestamp,
    };

    if (nextStravaLink && !selectedRace.stravaLinkFirstEnabledAt) {
      updates[`subscribedRaces.${selectedRace.legId}.stravaLinkFirstEnabledAt`] = timestamp;
    }

    try {
      await updateDoc(doc(db, "users", user.uid), updates);
    } catch (error) {
      console.error("Unable to update automatic race tracking", error);
    }
  }

  function getFinisherBadgeFilename(mimeType = "image/png") {
    const raceSlug =
      selectedRace?.raceInfo?.name
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "") || "challenge";

    const extensionMap = {
      "image/png": "png",
      "image/jpeg": "jpg",
      "image/jpg": "jpg",
      "image/webp": "webp",
      "image/gif": "gif",
      "image/svg+xml": "svg",
    };

    const extension = extensionMap[mimeType] || "png";
    return `${raceSlug}-finisher-badge.${extension}`;
  }

  function getFinisherBadgeDownloadURL() {
    if (!finisherBadge) return "";
    try {
      const badgeURL = new URL(finisherBadge);
      badgeURL.searchParams.set("response-content-disposition", `attachment; filename="${getFinisherBadgeFilename()}"`);
      return badgeURL.toString();
    } catch (err) {
      return finisherBadge;
    }
  }

  function downloadFinisherBadge() {
    const badgeURL = getFinisherBadgeDownloadURL();
    if (!badgeURL) return;
    const anchor = document.createElement("a");
    anchor.href = badgeURL;
    anchor.download = getFinisherBadgeFilename();
    anchor.target = "_blank";
    anchor.rel = "noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  function shareImageToFeed(imageUrl) {
    if (!imageUrl) return;
    navigate("/", { state: { createdImageUrl: imageUrl } });
  }

  const raceStart = new Date(selectedRace.raceInfo?.raceDateStart);
  const raceEnd = new Date(Date.parse(selectedRace.raceInfo?.raceDateEnd));
  const currentDate = new Date();
  const usesParticipantLocalDates = selectedRace.raceInfo?.activityDatePolicy === "participant_local_v1";
  const todayKey = getLocalTodayKey(currentDate.getTime(), userData?.timeZone || "");
  const raceStarted = usesParticipantLocalDates
    ? todayKey >= selectedRace.raceInfo?.activityStartDate
    : currentDate.getTime() - raceStart.getTime() > 0;
  const raceEnded = usesParticipantLocalDates
    ? todayKey > selectedRace.raceInfo?.activityEndDate
    : currentDate.getTime() - raceEnd.getTime() > 0;

  const raceActive = raceStarted && !raceEnded;

  // console.log(raceEnd)
  // const raceEnd = new Date("2023-03-19");
  // const diff = raceEnd.getTime() - currentDate.getTime();
  const diff = new Date(raceEnd.getTime() - currentDate.getTime());
  const diffAgo = new Date(currentDate.getTime() - raceEnd.getTime());
  const diffUntil = new Date(raceStart.getTime() - currentDate.getTime());

  // console.log(raceStarted, raceEnded);

  // let inputDefaultHour = currentDate.getHours() % 12;
  // inputDefaultHour = inputDefaultHour === 0 ? 12 : inputDefaultHour;
  // const inputDefaultMinute = currentDate.getMinutes();
  const inputDefaultValueString = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, "0")}-${currentDate
    .getDate()
    .toString()
    .padStart(2, "0")}`;

  const inputMinDateString = `${raceStart.getFullYear()}-${(raceStart.getMonth() + 1).toString().padStart(2, "0")}-${raceStart
    .getDate()
    .toString()
    .padStart(2, "0")}`;

  const inputMaxDate = raceEnd - currentDate > 0 ? currentDate : raceEnd;
  const inputMaxDateString = `${inputMaxDate.getFullYear()}-${(inputMaxDate.getMonth() + 1).toString().padStart(2, "0")}-${inputMaxDate
    .getDate()
    .toString()
    .padStart(2, "0")}`;

  const daysLeft = Math.floor(diff.getTime() / (1000 * 60 * 60 * 24));
  const hoursLeft = Math.floor((diff.getTime() % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutesLeft = Math.floor((diff.getTime() % (1000 * 60 * 60)) / (1000 * 60));

  const daysAgo = Math.floor(diffAgo.getTime() / (1000 * 60 * 60 * 24));
  const hoursAgo = Math.floor((diffAgo.getTime() % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutesAgo = Math.floor((diffAgo.getTime() % (1000 * 60 * 60)) / (1000 * 60));

  const daysUntil = Math.floor(diffUntil.getTime() / (1000 * 60 * 60 * 24));
  const hoursUntil = Math.floor((diffUntil.getTime() % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutesUntil = Math.floor((diffUntil.getTime() % (1000 * 60 * 60)) / (1000 * 60));

  const teamMembersArray = [];
  let teamDistance = 0;
  teamMembers.forEach((member, id) => {
    teamDistance += parseFloat(member.totalDistance);
    teamMembersArray.push({ id: id, data: member, captain: id === teamDoc?.owner });
  });
  if (teamDistance === 0) teamDistance = totalDistance;
  teamMembersArray.sort((a, b) => a.data.totalDistance < b.data.totalDistance);

  // function AddTp() {
  //   let newMem = teamMembers;
  //   let rand = Math.random();
  //   newMem.set(rand, {
  //     bibNumber: rand,
  //     firstName: "Tp",
  //     lastName: (rand * 10).toFixed(3),
  //     // times: {},
  //     totalDistance: (rand * 500 + 100).toFixed(2),
  //   });
  //   setTeamMembers((prev) => newMem);
  // }

  return (
    <div className="mx-auto max-w-3xl w-full mt-6">
      <RaceTitle selectedRace={selectedRace} />
      <div className="mb-3 pb-">
        <div className="grid grid-cols-2 gap-2 mt-3 mb-3">
          <div className="grid content-center col-span-2">
            <div className=" grid grid-cols-[24px,1fr] gap-x-2 items-center">
              <AccountIcon />
              <div>
                <p className="mb-0">
                  {userDetails.firstName} {userDetails.lastName}{" "}
                  <span className="whitespace-nowrap">
                    (BIB{" "}
                    {bibLink ? (
                      <a href={bibLink} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                        {selectedRace?.bibNumber}
                      </a>
                    ) : (
                      selectedRace?.bibNumber
                    )}
                    )
                  </span>
                </p>
              </div>
            </div>
            {teamDoc?.owner === user.uid && (
              <div className="flex gap-2">
                <p className="mb-0 mt-1 bg-red-600 text-white w-fit px-2 rounded-lg text-sm font-medium">Team Captain</p>
              </div>
            )}
          </div>
          <DropDownMenu
            trigger={({ ref, onClick, open }) => (
              <button
                ref={ref}
                onPointerDown={onClick}
                className="text-center whitespace-nowrap w-full h-min grid items-center hover:!bg-[#0F74C5] rounded-md text-sm font-medium text-white px-1 py-2"
                style={{ backgroundColor: colors.blue }}
              >
                Create Image
              </button>
            )}
            closeOnClick={false}
          >
            <CreateImage
              raceName={selectedRace?.raceInfo?.name}
              imagePreset={resolvedImagePreset}
              distance={selectedRace?.raceInfo?.distance}
            />
          </DropDownMenu>
          {!linkedDevice ||
            (linkedDevice === "none" && (
              <Link
                className="text-center whitespace-nowrap w-full h-min grid items-center hover:!bg-[#0F74C5] rounded-md text-sm font-medium text-white decoration-transparent px-1 py-2"
                to={"/account"}
                style={{ backgroundColor: colors.blue }}
              >
                Connect your Watch
              </Link>
            ))}
          {showTrackingToggle && (
            <Form.Group className="">
              <Form.Label className="h-full py-2 flex gap-1 flex-wrap align-items-center cursor-pointer" htmlFor="stravaCheck">
                <p className="m-0 text-center text-sm">Automatic Activities with your {linkedDevice}</p>
                <Form.Check
                  className="m-auto text-2xl"
                  name="stravaCheck"
                  id="stravaCheck"
                  type="switch"
                  reverse
                  checked={selectedRace.stravaLink !== false}
                  onChange={(event) => switchStravaCheck(event.target.checked)}
                />
              </Form.Label>
            </Form.Group>
          )}
        </div>

        <div className="flex gap-2  justify-evenly">
          <div className="flex w-full min-h-12 justify-between items-center">
            <Button
              variant={raceActive && !selectedRace?.completedDate && "primary"}
              className={`text-white w-full !text-sm !font-medium py-2 shrink-0 text-center !px-1 ${
                (!raceActive || selectedRace?.completedDate) && "!bg-slate-500 active:!bg-slate-500 hover:!bg-slate-500"
              }`}
              onClick={(e) => {
                e.preventDefault();
                if (raceActive && !selectedRace?.completedDate) setShowActivityForm(true);
              }}
            >
              Add Activity Manually
            </Button>
          </div>
          <div className="flex w-full min-h-12 justify-between items-center">
            <Link
              className="text-center whitespace-nowrap w-full h-full grid items-center hover:!bg-[#0F74C5] rounded-md text-sm font-medium text-white decoration-transparent px-1 py-2"
              style={{ backgroundColor: colors.blue }}
              to={`/leaderboard/${raceId}`}
            >
              View Leaderboard
            </Link>
          </div>
        </div>

        {/* COMPLETED RACE BADGE */}
        {/* {selectedRace?.completedDate && selectedRace?.raceInfo?.name === "Celebrate Canada 2023" && (
                <div className="px-3">
                  <Link to="https://mychallengecanada.ca/finisher/celebrate/" target="_blank">
                    <img className="w-52" src={`${process.env.PUBLIC_URL}/img/CCC 2023 - FINISHER.png`} alt="" />
                    <p>{`Create your finisher image ->`}</p>
                  </Link>
                </div>
              )} */}
        {/* {true && ( */}
        {selectedRace?.completedDate && finisherBadge && (presetFinisherBadge || raceDoc?.finisher_link) && (
          <div className="my-3 mx-auto w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-3 shadow-sm relative">
            <img className="w-52 max-w-full mx-auto" src={finisherBadge} alt="Finisher badge" />
            <div className="grid grid-cols-[1.35fr,1fr] gap-2 mt-3">
              <DropDownMenu
                closeOnClick={false}
                trigger={({ ref, onClick, open }) => (
                  <button
                    ref={ref}
                    onPointerDown={onClick}
                    className="min-h-11 rounded-lg px-3 py-2 text-sm font-medium text-white hover:opacity-95"
                    style={{ backgroundColor: colors.blue }}
                    aria-pressed={open}
                  >
                    Create Image
                  </button>
                )}
              >
                <CreateImage
                  raceName={selectedRace?.raceInfo?.name}
                  imagePreset={resolvedImagePreset}
                  finisher={true}
                  distance={selectedRace?.raceInfo?.distance}
                />
              </DropDownMenu>
              <button
                type="button"
                onClick={downloadFinisherBadge}
                className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Download
              </button>
            </div>
          </div>
        )}
      </div>
      {/* <div>
        <Link to={`/register/${raceData?.metadata?.url}/confirmation?race=${selectedRace.legId.split("_").pop()}`}>Confirmation page</Link>
      </div> */}
      {/* {showActivityForm && (
        <ActivityForm
          inputMinDateString={inputMinDateString}
          inputMaxDateString={inputMaxDateString}
          isSubmitting={isSubmitting}
        />
      )} */}
      <Modal
        show={showActivityForm}
        onHide={() => {
          if (submissionState === "submitting") {
            return;
          }
          closeActivityForm();
        }}
        onExited={handleActivityFormExited}
        backdrop={submissionState === "submitting" ? "static" : true}
        keyboard={submissionState !== "submitting"}
        className="!grid items-center"
        contentClassName="!border-none !bg-transparent"
      >
        <ActivityForm
          inputMinDateString={inputMinDateString}
          inputMaxDateString={inputMaxDateString}
          inputDefaultValueString={inputDefaultValueString}
          submissionState={submissionState}
          submitError={submitError}
          clearSubmitError={() => setSubmitError("")}
          showActivityForm={showActivityForm}
          closeActivityForm={closeActivityForm}
          onSubmitActivity={addTimeToRace}
          fixedChallengeIds={[selectedRace?.legId].filter(Boolean)}
          timeZone={userData?.timeZone || ""}
          origin={MANUAL_ACTIVITY_ORIGINS.CHALLENGE_DASHBOARD}
          title="Add Manual Activity"
        />
      </Modal>

      {/* OLD PERSONAL */}
      {/* <div className={`w-full mt-3 p-3 rounded-md text-white`} style={{ backgroundColor: colors.blue }}>
        <p className="font-medium">
          {userDetails.firstName} {userDetails.lastName}
        </p>
        <div className="grid grid-cols-3">
          <div>
            <p className="font-semibold">Distance so Far</p>
            <p className="mb-0">{totalDistance.toFixed(2)} km</p>
          </div>
          <div>
            <p className="font-semibold">Distance to Go</p>
            <p className="mb-0">{(parseInt(selectedRace.raceInfo?.distance) - totalDistance).toFixed(2)} km</p>
          </div>
          <div>
            {raceEnded ? (
              <>
                <p className="font-semibold">Ended Since</p>
                <p className="mb-0">
                  {daysAgo} days {hoursAgo} hours and {minutesAgo} minutes
                </p>
              </>
            ) : raceStarted ? (
              <>
                <p className="font-semibold">Days Left</p>
                <p className="mb-0">
                  {daysLeft} days {hoursLeft} hours and {minutesLeft} minutes
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold">Time until start</p>
                <p className="mb-0">
                  {daysUntil} days {hoursUntil} hours and {minutesUntil} minutes
                </p>
              </>
            )}
          </div>
        </div>
      </div> */}
      {/* OLD TEAMS */}
      {/* {selectedRace.teamName !== "noTeam" && (
        <div className="w-full border-2 mt-3 p-3 rounded-md border-green-400">
          <p>Team {selectedRace.teamName}</p>
          <div className="grid grid-cols-3">
            <div>
              <p>Distance so Far</p>
              <p className="mb-0">{teamDistance.toFixed(2)}km</p>
            </div>
            <div>
              <p>Distance to Go</p>
              <p className="mb-0">{(raceDistance - teamDistance).toFixed(2)}km</p>
            </div>
            <div>
              {raceEnded ? (
                <>
                  <p className="font-semibold">Ended Since</p>
                  <p className="mb-0">
                    {daysAgo} days {hoursAgo} hours and {minutesAgo} minutes
                  </p>
                </>
              ) : raceStarted ? (
                <>
                  <p className="font-semibold">Days Left</p>
                  <p className="mb-0">
                    {daysLeft} days {hoursLeft} hours and {minutesLeft} minutes
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold">Time until start</p>
                  <p className="mb-0">
                    {daysUntil} days {hoursUntil} hours and {minutesUntil} minutes
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )} */}

      {/* {selectedRace.teamName !== "noTeam" && (
      <div className="w-full border-2 mt-3 p-3 rounded-md border-green-400">
        <p>Team Members</p>
        <div className="flex flex-col">
          {teamMembersArray.map((member) => {
            let memberDistance = parseFloat(member.data.totalDistance);
            const progress = (memberDistance * 100) / parseInt(selectedRace.raceInfo?.distance);
            return (
              <div className="grid grid-cols-3 gap-3" key={member.data.bibNumber}>
                <p>
                  {member.data.firstName} {member.data.lastName}
                </p>
                <p className="mb-0">{memberDistance.toFixed(2)} Km</p>
                <p className="mb-0">% {progress.toFixed(2)}</p>
              </div>
            );
          })}
        </div>
      </div>
    )} */}

      {/* {raceActive && (
        <h4 className="text-lg font-medium text-center w-full">
          {daysLeft} days {hoursLeft} hours and {minutesLeft} minutes left
        </h4>
      )} */}

      {/* CHALLENGE ENDED OR NOT STARTED TEXT */}
      {!raceActive && raceStarted && <h4 className="text-center">This challenge has ended.</h4>}
      {!raceStarted && (
        <p className="text-center mt-3 block m-0 text-green-700 font-medium">{`This challenge hasn't started yet. You can start uploading your activities on ${selectedRace?.raceInfo?.userStartDate}`}</p>
      )}

      {/* {selectedRace.teamName !== "noTeam" && <button onClick={AddTp}>Add Tp</button>} */}
      {selectedRace && userTimesDoc && (
        <div className="shadow">
          <ProgressComponent
            selectedRace={selectedRace}
            participants={selectedRace.teamName !== "noTeam" ? teamMembersArray : [{ id: userTimesDoc?.id, data: userTimesDoc?.data() }]}
            teamDoc={teamDoc}
          />
          {raceActive && (
            <div className="grid grid-cols-3 justify-items-center pb-3 px-1 gap-x-1">
              <p className="font-normal text-sm">Time Left</p>
              <p className="font-normal text-sm">Distance Left</p>
              <p className="font-normal text-sm">To Go</p>
              <div className="w-full shadowBox grid items-center py-2">
                <span className="m-0 text-xs text-center">
                  {daysLeft} Day / {hoursLeft} hrs / {minutesLeft} min
                </span>
              </div>
              <div className="w-full shadowBox grid items-center py-2">
                <span className="m-0 text-xs text-center">
                  {(parseInt(selectedRace.raceInfo?.distance) - teamDistance).toLocaleString(undefined, {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  km
                </span>
              </div>
              <div className="w-full shadowBox grid items-center py-2">
                <span className="text-xs  text-center">
                  {((parseInt(selectedRace.raceInfo?.distance) - teamDistance) / daysLeft / (teamMembersArray.length | 1)).toFixed(2)}
                  km / day / member
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Personal Progress */}
      {/* {selectedRace.teamName === "noTeam" && (
        <PersonalProgress
          progressDistance={totalDistance}
          goalDistance={parseInt(selectedRace.raceInfo?.distance)}
          timeLeft={{ days: daysLeft, hours: hoursLeft, minutes: minutesLeft }}
          raceActive={raceActive}
        />
      )} */}

      {/* Team Progress */}
      {/* {selectedRace.teamName !== "noTeam" && (
        <TeamProgress
          progressDistance={teamDistance}
          goalDistance={parseInt(selectedRace.raceInfo?.distance)}
          teamName={selectedRace.teamName}
          teamMembers={teamMembersArray}
          timeLeft={{ days: daysLeft, hours: hoursLeft, minutes: minutesLeft }}
          raceActive={raceActive}
        />
      )} */}

      {typeof raceDoc?.milestones === typeof "" && fullscreenMedalRef.current && (
        <MilestonesFeed
          selectedRace={selectedRace}
          milestonesId={raceDoc.milestones}
          fullscreenMedalRef={fullscreenMedalRef}
          canSocialFeed={canSocialFeed}
          shareImageToFeed={shareImageToFeed}
        />
      )}

      <ActivityFeed activityFeed={activityFeed} getActivity={getActivity} selectedRace={selectedRace} totalDistance={totalDistance} />

      <div
        ref={fullscreenMedalRef}
        className="bg-slate-500 bg-opacity-50 fixed grid place-items-center top-0 inset-0 h-screen cursor-pointer py-3"
        style={{ display: "none" }}
        onClick={(e) => {
          if ((e.currentTarget === e.target) | true) {
            e.currentTarget.style.display = "none";
          }
        }}
      >
        <img className="mx-auto w-[calc(100vmin-24px)] h-[calc(100vmin-24px)] rounded-md anim-card object-contain" src="" />
      </div>
    </div>
  );
}

function MilestonesFeed({ selectedRace, milestonesId, fullscreenMedalRef, canSocialFeed, shareImageToFeed }) {
  const milestones = selectedRace.milestones;
  const [milestonesData, setMilestonesData] = useState(null);

  function openFullscreenImage(imageUrl, imageTitle) {
    const imageRef = fullscreenMedalRef.current.firstChild;
    fullscreenMedalRef.current.style.display = "grid";
    imageRef.src = imageUrl;
    imageRef.alt = imageTitle;
    imageRef.title = imageTitle;
  }

  useEffect(() => {
    async function getMilestonesDoc() {
      const milestonesDoc = await getDoc(doc(db, "milestones", milestonesId));
      if (!milestonesDoc.exists()) {
        return;
      }
      setMilestonesData(milestonesDoc.data());
    }
    getMilestonesDoc();
  }, []);
  return (
    <div className="w-full flex flex-col">
      <h3 className="mt-4">Milestones</h3>
      {milestonesData?.header && (
        <div key={milestonesData.header.name} className="flex flex-col mt-4 gap-0">
          <div className="flex gap-2 mb-1">
            <h6 className="font-semibold text-base mb-0">
              {milestonesData.header.name} - {milestonesData.header.location}
            </h6>
          </div>
          <div className="flex gap-2 ">
            {milestonesData.header?.image_url && (
              <div className="flex flex-col items-start shrink-0">
                <img
                  className="w-24 h-24 cursor-pointer rounded-sm"
                  src={milestonesData.header.image_url}
                  alt={`Milestone ${milestonesData.header.name} image`}
                  onClick={() => {
                    openFullscreenImage(milestonesData.header.image_url, milestonesData.header.name);
                  }}
                />
                {canSocialFeed && (
                  <button
                    type="button"
                    onClick={() => shareImageToFeed(milestonesData.header.image_url)}
                    className="mt-2 w-24 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Share
                  </button>
                )}
              </div>
            )}
            <div className="w-full">
              {milestonesData.header?.description && <p className="m-0">{milestonesData.header?.description}</p>}
              {milestonesData.header?.link && (
                <a className="m-0 cursor-pointer break-all" href={milestonesData.header?.link} target="_blank" rel="noreferrer">
                  {milestonesData.header?.link}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      {typeof milestones === typeof {} &&
        typeof milestonesData?.milestones === typeof [] &&
        milestonesData.milestones.map((milestone) => {
          // console.log(milestone);
          if (milestones[milestone.name]) {
            return (
              <div key={milestone.name} className="flex flex-col mt-4 gap-0">
                <div className="flex gap-2 mb-1">
                  <h6 className="font-semibold text-base mb-0">
                    {milestone.name} - {milestone.location}
                  </h6>
                  <p className="m-0">{milestone.distance} km</p>
                </div>
                <div className="flex gap-2 ">
                  {milestone?.image_url && (
                    <div className="flex flex-col items-start shrink-0">
                      <img
                        className="w-24 h-24 cursor-pointer rounded-sm object-contain"
                        src={milestone.image_url}
                        alt={`Milestone ${milestone.name} image`}
                        onClick={() => {
                          openFullscreenImage(milestone.image_url, milestone.name);
                        }}
                      />
                      {canSocialFeed && (
                        <button
                          type="button"
                          onClick={() => shareImageToFeed(milestone.image_url)}
                          className="mt-2 w-24 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Share
                        </button>
                      )}
                    </div>
                  )}
                  <div className="w-full">
                    {milestone?.description && <p className="m-0">{milestone?.description}</p>}
                    {milestone?.link && (
                      <a className="m-0 cursor-pointer break-all" href={milestone?.link} target="_blank" rel="noreferrer">
                        {milestone?.link}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          }
          return;
        })}
    </div>
  );
}

function ProgressComponent({ selectedRace, participants, teamDoc }) {
  const { user } = useUserContext();
  const isTeam = selectedRace.teamName !== "noTeam";
  const progressTabs = ["Total", "Last 7 Days", "Today"];
  const { colors } = useThemeContext();
  const [selectedTab, setSelectedTab] = useState(progressTabs[0]);
  const selectedTabIndex = progressTabs.indexOf(selectedTab);
  const contrastingColors = generateContrastingColors(participants.length);
  const progressTable = participants.map((participant, participantIndex) => {
    let data = participant.data;
    let totalProgress = data.totalDistance ? data.totalDistance : 0;
    let weekProgress = data.times
      ? Object.values(data.times).reduce((acc, time) => {
          let activityDate = new Date(time.activity.date);
          let activityDistance = parseFloat(time.distance);
          if (isWithinLast7Days(activityDate)) return acc + activityDistance;
          return acc;
        }, 0)
      : 0;
    let dailyProgress = data.times
      ? Object.values(data.times).reduce((acc, time) => {
          let activityDate = new Date(time.activity.date);
          let activityDistance = parseFloat(time.distance);
          if (isWithinLastDay(activityDate)) return acc + activityDistance;
          return acc;
        }, 0)
      : 0;
    let activitiesTable = data.times
      ? [
          Object.values(data.times).map((time) => {
            return { date: new Date(time.activity.date), distance: parseFloat(time.distance) };
          }),
          Object.values(data.times)
            .filter((time) => {
              return isWithinLast7Days(new Date(time.activity.date));
            })
            .map((time) => {
              return { date: new Date(time.activity.date), distance: parseFloat(time.distance) };
            }),
          Object.values(data.times)
            .filter((time) => {
              return isSameDay(new Date(time.activity.date));
            })
            .map((time) => {
              return { date: new Date(time.activity.date), distance: parseFloat(time.distance) };
            }),
        ]
      : [[], [], []];
    return {
      captain: participant?.captain,
      name: data.firstName + " " + data.lastName,
      firstName: data.firstName,
      lastName: data.lastName,
      progress: [totalProgress, weekProgress, dailyProgress],
      activities: activitiesTable,
      color: contrastingColors[participantIndex],
    };
  });

  const sortedProgressTable = progressTable.sort((a, b) => {
    return b.progress[selectedTabIndex] - a.progress[selectedTabIndex];
  });

  const totalDistance = progressTable.reduce((acc, current) => {
    return acc + parseFloat(current.progress[0]);
  }, 0);
  const weekDistance = progressTable.reduce((acc, current) => {
    return acc + current.progress[1];
  }, 0);
  const todayDistance = progressTable.reduce((acc, current) => {
    return acc + current.progress[2];
  }, 0);

  const distanceTable = [totalDistance, weekDistance, todayDistance];

  function isSameDay(date1, date2 = new Date()) {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
  }

  function isWithinLastDay(date) {
    const now = new Date(); // Current date and time
    const sevenDaysAgo = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000); // 1 day ago from now
    return date >= sevenDaysAgo && date <= now;
  }
  function isWithinLast7Days(date) {
    const now = new Date(); // Current date and time
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); // 7 days ago from now
    return date >= sevenDaysAgo && date <= now;
  }

  // Timeline setup
  const timelineCanvasRef = useRef();

  useEffect(() => {
    if (!timelineCanvasRef.current) return;
    let canvas = document.createElement("canvas");
    canvas = timelineCanvasRef.current;
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.width / 4;
    let ctx = document.createElement("canvas").getContext("2d");
    ctx = canvas.getContext("2d");

    const raceStart = new Date(selectedRace.raceInfo.raceDateStart);
    const raceEnd = new Date(selectedRace.raceInfo.raceDateEnd);

    const timelineStart = raceStart;
    const timelineEnd = raceEnd.getTime() > Date.now() ? raceEnd : new Date();

    // console.log(timelineEnd.getTime() - timelineStart.getTime());
    // console.log(1 * 24 * 60 * 60 * 1000); // 1 day
    // console.log("days", (timelineEnd.getTime() - timelineStart.getTime()) / (1 * 24 * 60 * 60 * 1000));
    // console.log(canvas.width);

    for (let i = timelineStart.getTime(); i < timelineEnd.getTime(); i += 30 * 24 * 60 * 60 * 1000) {
      let x = getXCoord(i);
      ctx.fillRect(x, 0, 1, canvas.height);
    }

    function getXCoord(i) {
      let x = ((i - timelineStart.getTime()) * 100) / (timelineEnd.getTime() - timelineStart.getTime()) / 100;
      x *= canvas.width;
      return x;
    }
    progressTable.forEach((part) => {
      part.activities[0].forEach((time) => {
        let date = time.date;
        let x = getXCoord(date.getTime());
        let y = canvas.height - (time.distance / totalDistance) * canvas.height;
        ctx.fillRect(x, y, 2, 2);
      });
    });
  }, [timelineCanvasRef.current]);

  return (
    <div className=" py-3 rounded-md text-center">
      <div className="flex justify-center items-center gap-2">
        {!isTeam && <h5 className="font-semibold">Your</h5>}
        <h5 className="font-semibold">Progress</h5>
        {isTeam && <h5 className="">{selectedRace.teamName}</h5>}
      </div>
      {user.uid === teamDoc?.owner && (
        <div className="flex justify-center items-center gap-2">
          <p className="mb-2 font-medium text-red-700">Team Code: {teamDoc.code}</p>
        </div>
      )}
      <div className="flex justify-between px-2 gap-2">
        {progressTabs.map((text) => {
          return <ProgressButton key={text} text={text} active={selectedTab === text} setSelectedTab={setSelectedTab} />;
        })}
      </div>
      {/* <div className="flex flex-col">
        <p>Timeline</p>
        <div><canvas ref={timelineCanvasRef} className="w-full" /></div>
      </div> */}
      <div>
        {/* <h6 className="mt-2">
          {selectedTab} - {distanceTable[selectedTabIndex].toFixed(2)} km
        </h6> */}
        <div className="flex gap-x-0 flex-row h-[160px] mx-2 mt-3">
          {true && (
            <div
              className="flex flex-col gap-y-2 w-full overflow-y-scroll pr-2 pb-3 pt-1"
              style={{ scrollbarWidth: "thin", scrollbarColor: `${colors.blue} #D9D9D9` }}
            >
              {sortedProgressTable.map((participant) => {
                return (
                  <div
                    key={participant.name}
                    className="shadowBox grid grid-cols-2 align-middle px-2 py-1"
                    style={{ backgroundImage: `linear-gradient(to left, ${participant.color} 10%, transparent 65%)` }}
                  >
                    <span className="text-xs text-left overflow-hidden">
                      {participant.firstName}{" "}
                      {participant.captain && (
                        <span className="mb-0 mt-1 bg-red-600 text-white w-fit px-2 rounded-lg text-xs font-medium">Captain</span>
                      )}
                    </span>

                    <span className="text-xs  text-right grid items-center">
                      {parseFloat(participant.progress[selectedTabIndex]).toFixed(0)}km
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="w-full grid items-center">
            {/* <ProgressCircleColored
              index={selectedTabIndex}
              // distanceTable={[parseFloat(selectedRace.raceInfo.distance), distanceTable[1], distanceTable[2]]}
              distanceTable={distanceTable}
              progressTable={sortedProgressTable}
            /> */}
            <ProgressCircleColoredCanvas
              index={selectedTabIndex}
              distanceTable={[parseFloat(selectedRace.raceInfo.distance), distanceTable[1], distanceTable[2]]}
              // distanceTable={distanceTable}
              progressTable={sortedProgressTable}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ProgressButton({ text, setSelectedTab, active = false }) {
  return (
    <button
      className="w-full cursor-pointer px-1 py-2 rounded-md font-medium"
      style={{ backgroundColor: active ? "#D9D9D9D9" : "transparent" }}
      onClick={function () {
        setSelectedTab(text);
      }}
    >
      {text}
    </button>
  );
}

function generateContrastingColors(count) {
  if (count < 1) return [];
  const colors = [];
  const hueStep = 360 / count;
  for (let i = 0; i < count; i++) {
    const hue = i * hueStep;
    const color = `hsl(${hue}, 65%, 74%)`;
    colors.push(color);
  }
  return colors;
}

function PersonalProgress({ progressDistance = 0, goalDistance = 1, timeLeft = { days: 1, hours: 0, minutes: 0 }, raceActive }) {
  const localeOptions = { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 };
  const progressPercent = (progressDistance * 100) / goalDistance;
  const distanceLeft = goalDistance - progressDistance;
  return (
    <div className="shadow py-3 px-3 rounded-md text-center">
      <div className="flex items-stretch mb-3 max-w- mx-auto">
        <div className="w-full flex flex-col justify-center gap-1">
          <h5 className="w-full font-semibold">Your Progress</h5>
          <h5>{progressDistance.toLocaleString(undefined, localeOptions)} km</h5>
        </div>
        <ProgressCirlce progressPercent={progressPercent} />
      </div>
      {/* <h5>{distanceLeft.toLocaleString(undefined, localeOptions)} km left</h5> */}
      {raceActive && (
        <div className="grid grid-cols-3 justify-items-center">
          <h6 className="font-semibold">Time Left</h6>
          <h6 className="font-semibold">Distance Left</h6>
          <h6 className="font-semibold">To Go</h6>
          <div className="w-fit">
            <p className="m-0 text-left">{timeLeft.days} Days</p>
            <p className="m-0 text-left">{timeLeft.hours} Hours</p>
            <p className="m-0 text-left">{timeLeft.minutes} Minutes</p>
          </div>
          <div className="w-fit">
            <p className="m-0">{distanceLeft.toLocaleString(undefined, localeOptions)}km</p>
          </div>
          <div className="w-fit">{(distanceLeft / timeLeft.days).toFixed(2)}km/day</div>
        </div>
      )}
    </div>
  );
}

function TeamProgress({ progressDistance, goalDistance, teamMembers, teamName, timeLeft = { days: 1, hours: 0, minutes: 0 }, raceActive }) {
  // console.log(teamMembers);
  // for (let m = 0; m <= 1; m++) {
  //   teamMembers.push({
  //     id: Math.random(),
  //     data: {
  //       bibNumber: Math.random() * 100,
  //       firstName: "test",
  //       lastName: "name " + m,
  //       totalDistance: Math.random() * 200,
  //     },
  //   });
  // }
  // progressDistance = teamMembers.reduce((acc, member) => {
  //   return member.data.totalDistance + acc;
  // }, 0);
  // teamMembers.sort((a, b) => a.data.totalDistance < b.data.totalDistance);
  const localeOptions = { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 };
  const progressPercent = (progressDistance * 100) / goalDistance;
  const distanceLeft = goalDistance - progressDistance;
  return (
    <div className="shadow py-3 px-3 rounded-md text-center">
      <div className="mb-3 grid gap-x-3 grid-cols-1 sm:grid-cols-2 items-center grid-flow-dense">
        <div className="order-1 sm:col-span-2 mb-3">
          <h5 className="w-full font-semibold">Team Progress</h5>
          <h6 className="m-0">{teamName}</h6>
        </div>
        {/* <div className="w-full flex flex-col gap-1 sm:row-start-2 order-3"> */}
        {/* <div> */}
        {teamMembers.map((member) => {
          const data = member.data;
          const memberProgress = (data.totalDistance * 100) / goalDistance;
          return (
            <div key={data.bibNumber} className="border-t flex items-center justify-between px-1 pt-1 order-3">
              <span className="justify-self-start">
                {data.firstName} {data.lastName}
              </span>
              <div className="grid grid-rows-2">
                <span className="justify-self-end">{data.totalDistance.toLocaleString(undefined, localeOptions)}km</span>
                <span className="justify-self-end">{memberProgress.toLocaleString(undefined, localeOptions)}%</span>
              </div>
            </div>
          );
        })}
        {/* <hr></hr>
            <div className="grid grid-cols-2 ">
              <span className="justify-self-start">TOTAL</span>
              <span className="justify-self-end">{progressDistance.toLocaleString(undefined, localeOptions)}km</span>
            </div> */}
        {/* </div> */}
        {/* </div> */}
        <ProgressCirlce progressPercent={progressPercent} />
      </div>
      <hr />
      {raceActive && (
        <div className="grid grid-cols-3 gap-x-2 justify-items-center">
          <h6 className="font-semibold">Time Left</h6>
          <h6 className="font-semibold">Distance Left</h6>
          <h6 className="font-semibold">To Go</h6>
          <div className="w-fit">
            <p className="m-0 text-left">{timeLeft.days} Days</p>
            <p className="m-0 text-left">{timeLeft.hours} Hours</p>
            <p className="m-0 text-left">{timeLeft.minutes} Minutes</p>
          </div>
          <div className="w-fit">
            <p className="m-0">{distanceLeft.toLocaleString(undefined, localeOptions)}km</p>
          </div>
          <div className="w-fit">
            <p className="m-0">{(distanceLeft / timeLeft.days / teamMembers.length).toFixed(2)}km/day per member</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ProgressCircleColoredCanvas({ index, distanceTable, progressTable }) {
  const canvasRef = useRef();
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const nextCanvasSize = Math.max(1, Math.min(canvas.clientWidth || canvas.width || 0, canvas.clientHeight || canvas.height || canvas.clientWidth || 0));
    let canvasSize = nextCanvasSize;
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    // ctx.fillStyle = "black";
    // ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 18;
    let x = canvas.width / 2;
    let y = canvas.height / 2;
    let maxDistance = distanceTable[index];
    let lastAngle = -90;

    ctx.beginPath();
    ctx.strokeStyle = "#AAA3";
    ctx.arc(x, y, x - ctx.lineWidth / 2, toRad(0), toRad(360));
    ctx.stroke();

    const progressText = progressTable.reduce((total, participant) => {
      const distance = Number.parseFloat(participant.progress[index]);
      return total + (Number.isFinite(distance) ? distance : 0);
    }, 0);

    for (let a = 0; a < progressTable.length; a++) {
      const participant = progressTable[a];
      const participantProgress = participant.progress[index] / maxDistance;
      ctx.beginPath();
      ctx.strokeStyle = participant.color;
      ctx.fillStyle = participant.color;
      let nextAngle = participantProgress * 360 + lastAngle;
      nextAngle = Math.min(268, nextAngle);
      if (nextAngle === lastAngle) continue;
      ctx.arc(x, y, x - ctx.lineWidth / 2, toRad(lastAngle), toRad(nextAngle));
      ctx.stroke();
      // ctx.lineTo(x, y);
      // ctx.fill();
      if (nextAngle === 268) break;
      lastAngle += participantProgress * 360 + 2;
    }

    ctx.fillStyle = "black";
    ctx.font = "500 16px montserrat";
    ctx.textAlign = "center";
    ctx.fillText(progressText.toFixed(1) + " km", x, y + 8);

    if (index == 0) {
      if (distanceTable[0] !== 0) {
        let progressPercent = (progressText / distanceTable[0]) * 100;
        if (!isFinite(progressPercent)) {
          progressPercent = 0;
        }
        ctx.font = "500 12px montserrat";
        ctx.textAlign = "center";
        ctx.fillText(progressPercent.toFixed(1) + " %", x, y + 16 + 8);
      }
    }

    function toRad(deg) {
      return (deg * Math.PI) / 180;
    }
  }, [distanceTable, index, progressTable]);
  return (
    <div className="w-full grid items-center">
      <canvas ref={canvasRef} className="mx-auto"></canvas>
    </div>
  );
}

function ProgressCircleColored({ index, distanceTable, progressTable }) {
  let maxDistance = distanceTable[index];
  return (
    <div className="w-full grid items-center">
      <style>
        {`@keyframes grow {
          from {
            stroke-dashoffset: -100;
          }

          to {
            stroke-dashoffset: ${Math.max(-100 - 100, -200)};
          }
        }
        `}
      </style>
      <svg className="mx-auto " width="30vmin" height="30vmin" viewBox="0 0 170 170" xmlns="http://www.w3.org/2000/svg">
        <circle
          cx="50%"
          cy="50%"
          r="40%"
          transform="rotate(-90, 85, 85)"
          stroke="#AAA"
          strokeWidth="10%"
          opacity={0.22}
          fill="none"
          pathLength="100"
          style={{
            strokeDasharray: "100 100",
            strokeDashoffset: Math.max(-100 - 100, -200),
          }}
        />
        {}
        {progressTable.map((participant, Pindex) => {
          const participantProgress = ((participant.progress[index] / maxDistance) * 100) | 0;
          let previousProgress = 0;
          for (let p = 0; p < Pindex; p++) {
            previousProgress += progressTable[p].progress[index];
          }
          previousProgress /= maxDistance;
          previousProgress = previousProgress ? previousProgress : 0;
          //console.log("participant ", Pindex);
          //console.log("prev progress", previousProgress);
          return (
            <circle
              key={participant.name + "-progress-circle"}
              cx="50%"
              cy="50%"
              r="40%"
              transform={`rotate(${previousProgress * 360 - 90}, 85, 85)`}
              stroke={participant.color}
              strokeWidth="10%"
              opacity={1}
              fill="none"
              pathLength="100"
              style={{
                strokeDasharray: "100 100",
                strokeDashoffset: Math.max(-100 - participantProgress, -200),
              }}
            />
          );
        })}
      </svg>
    </div>
  );
}

function ProgressCirlce({ progressPercent = 0 }) {
  const { colors } = useThemeContext();
  return (
    <div className="w-full grid items-center order-2 sm:col-start-2 sm:row-span-3">
      <style>
        {`@keyframes grow {
          from {
            stroke-dashoffset: -100;
          }

          to {
            stroke-dashoffset: ${Math.max(-100 - progressPercent, -200)};
          }
        }
        `}
      </style>
      <svg className="mx-auto " width="30vmin" height="30vmin" viewBox="0 0 170 170" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50%" cy="50%" r="40%" stroke="black" opacity={0.2} strokeWidth="10%" fill="none" />
        <circle
          cx="50%"
          cy="50%"
          r="40%"
          transform="rotate(-90, 85, 85)"
          stroke={colors.blue}
          strokeWidth="10%"
          opacity={0.5}
          fill="none"
          pathLength="100"
          style={{
            strokeDasharray: "100 100",
            strokeDashoffset: Math.max(-100 - progressPercent, -200),
            // strokeDashoffset: -100,
            // rotate: "-90deg",
            // transformOrigin: "center",
          }}
        />
        <circle
          cx="50%"
          cy="50%"
          r="40%"
          transform="rotate(-90, 85, 85)"
          stroke={colors.blue}
          strokeWidth="10%"
          fill="none"
          pathLength="100"
          style={{
            strokeDasharray: "100 100",
            // strokeDashoffset: Math.max(-100 - progressPercent, -200),
            strokeDashoffset: -100,
            // rotate: "-90deg",
            // transformOrigin: "center",
            animation: `grow 5000ms ease-out 1s forwards`,
          }}
        />
        <text
          x="50%"
          y="50%"
          dy="0.25rem"
          dominantBaseline="middle"
          textAnchor="middle"
          fill="black"
          fontSize="1.25rem"
          className="leading-none font-bold"
        >
          {progressPercent.toFixed(1)}%
        </text>
      </svg>
    </div>
  );
}
